/* ============================================================================
   kitsters-cart-bridge.js — the one place that knows which cart drawer we have
   ============================================================================
   Eleven Kitsters scripts add to cart. On India every one of them reaches
   directly into Kalles: it queries for Kalles' cart-drawer custom element by
   tag name, reads a `section-id` attribute off it, and puts that single id into
   the /cart/add.js payload's `sections` array.

   (The tag name is spelled out nowhere in this tree on purpose --
   scripts/check-no-kalles.py is a content scan and cannot tell a comment from
   code, so even quoting the old selector to explain it trips the guard. That is
   the correct trade-off; write around it.)

   That is eleven copies of one assumption about a vendor element, which is why
   root CLAUDE.md §10.21 says "never hand-roll an ATC handler" after four files
   grew variants and two of them were broken. Rather than port that mistake to a
   second store with a different drawer, the assumption lives here once and every
   caller becomes drawer-agnostic.

   WHY THE SHAPE IS DIFFERENT ON DAWN, AND WHY THE CONTRACT IS THE SAME
   Kalles publishes one section id on an attribute and expects a `cart:update`
   event carrying the add response. Dawn's <cart-drawer> publishes a LIST via
   getSectionsToRender() -- 'cart-drawer' and 'cart-icon-bubble' -- and re-renders
   through renderContents(response), which also opens the drawer. Different
   mechanics, identical contract: ask what sections to request, then hand the
   response back. So callers change one line and nothing else.

   Reading the list off the drawer instead of hardcoding it matters: Dawn's own
   product-form.js does exactly the same, so a theme setting or a Dawn upgrade
   that changes which sections re-render is picked up here for free.

   §10.21 ALSO APPLIES ON DAWN, IN A DIFFERENT FORM
   Kalles' listener destructures `cartData` on its first line, so an event
   without it threw and the item was added but never appeared. Dawn's equivalent
   trap is `renderContents()`, which reads parsedState.sections[...] with no
   guard -- so handing it a response with no `sections` key throws exactly the
   same way. addToCart() below only calls it when sections came back, and falls
   back to a plain count refresh otherwise. Both paths are exercised in the
   Phase 2 tests.
   ========================================================================== */
(function () {
  'use strict';

  function drawer() {
    return document.querySelector('cart-drawer');
  }

  /**
   * Section ids to request in a /cart/add.js payload.
   * Read from the drawer so this tracks Dawn rather than duplicating its list.
   * Returns [] when there is no drawer (e.g. cart-page-only themes) — an empty
   * `sections` array is valid and simply returns no rendered HTML.
   */
  function sectionsToRender() {
    var d = drawer();
    if (!d || typeof d.getSectionsToRender !== 'function') return [];
    try {
      return d.getSectionsToRender().map(function (s) { return s.id; });
    } catch (e) {
      return [];
    }
  }

  /**
   * Hand an add-response back to the cart UI.
   * With sections present, Dawn re-renders and opens the drawer itself.
   * Without them the add still happened, so refresh the count rather than
   * leaving a stale badge — never default a count to 0 (§10.21).
   */
  /* ── The CART PAGE is a second cart UI, and it needs the same treatment ──
     /cart renders <cart-items id="main-cart-items"> and #main-cart-footer, and
     Dawn's cart.js refreshes them through the Section Rendering API. The drawer
     is ALSO on this page (layout/theme.liquid renders it unconditionally), so
     an add made from /cart that went down the drawer path would both leave the
     page's own line-item table stale AND slide the drawer open on top of the
     cart the shopper is already looking at.

     So the bridge branches on which cart UI is on screen. Same reason it owns
     the drawer contract: one place decides, and every caller -- the gift-wrap
     band, a future upsell, anything -- is correct wherever it is rendered. */
  function cartPageSections() {
    var out = [];
    ['main-cart-items', 'main-cart-footer'].forEach(function (id) {
      var el = document.getElementById(id);
      /* `section` is the Shopify section id (dataset.id), which is what
         ?sections= expects; `id` is the DOM node we write back into. */
      if (el && el.dataset && el.dataset.id) out.push({ node: el, section: el.dataset.id });
    });
    return out;
  }

  function onCartPage() {
    return cartPageSections().length > 0;
  }

  function innerHTMLOf(html, selector) {
    var found = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return found ? found.innerHTML : null;
  }

  /* Re-render the cart page in place. `.js-contents` is Dawn's own swap target
     on both sections -- matching it keeps this working across a Dawn upgrade. */
  function refreshCartPage() {
    var secs = cartPageSections();
    if (!secs.length) return refreshCount();
    return fetch('/cart?sections=' + secs.map(function (s) { return s.section; }).join(','))
      .then(function (r) { return r.json(); })
      .then(function (rendered) {
        secs.forEach(function (s) {
          var html = rendered[s.section];
          if (!html) return;
          var inner = innerHTMLOf(html, '.js-contents');
          var target = s.node.querySelector('.js-contents');
          if (inner !== null && target) target.innerHTML = inner;
        });
        return refreshCount();
      })
      .catch(function () { return refreshCount(); });
  }

  function notify(addResponse) {
    /* On /cart the page's own line-item table is the cart the shopper is
       looking at; refresh that and leave the drawer shut. */
    if (onCartPage()) {
      refreshCartPage();
      return;
    }
    var d = drawer();
    if (d && addResponse && addResponse.sections) {
      try {
        d.renderContents(addResponse);
        /* NOT `return`. renderContents() re-renders Dawn's OWN sections --
           'cart-drawer' and 'cart-icon-bubble' -- and this store does not
           render cart-icon-bubble at all: header-group.json holds one section,
           `kitsters_header`, whose badge is `.kh-hdr__cart-count[data-cart-count]`
           and which redraws only on `cart:count:update` (kitsters-header.js).
           So Dawn's re-render fixes the DRAWER and leaves the header badge
           stale. Refreshing the count on BOTH paths is what makes every caller
           correct at once -- kitsters-supplies.js used the bridge from day one
           and still had to fetch /cart.js itself to move its badge. */
      } catch (e) {
        /* fall through to the count refresh below */
      }
    }
    refreshCount();
  }

  function refreshCount() {
    fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root + 'cart.js' : '/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        /* cart:count:update with { item_count } is the sitewide contract —
           kitsters-header.js and kitsters-favicon-cart-count.js listen to it,
           and every dispatcher in this tree sends it. The bridge's original
           'kitsters:cart:count' event had ZERO listeners, so this refresh
           never actually updated the badge. */
        document.dispatchEvent(new CustomEvent('cart:count:update', {
          detail: { item_count: cart.item_count }
        }));
      })
      .catch(function () { /* a failed count refresh must not break the add */ });
  }

  /**
   * Add one line and update the cart UI. Returns the add response.
   * Callers that need their own success/failure UI should use the promise;
   * the drawer is handled either way.
   */
  function addToCart(payload) {
    var body = Object.assign({}, payload);
    /* Don't ask the server to render the DRAWER's sections when we are on
       /cart -- notify() discards them there and refreshes the page's own
       sections instead, so requesting them is work nobody reads. */
    var sections = onCartPage() ? [] : sectionsToRender();
    if (sections.length) body.sections = sections;

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (r) {
        return r.json().then(function (data) {
          // /cart/add.js answers 422 with a JSON body describing the reason
          // (sold out, unavailable). Surface that rather than a generic failure.
          if (!r.ok) throw Object.assign(new Error(data.description || data.message || 'Add to cart failed'), { cartError: data });
          return data;
        });
      })
      .then(function (data) { notify(data); return data; });
  }

  /**
   * Re-render the cart UI from the server. For the case where the cart changed
   * somewhere this page cannot see — a checkout overlay the shopper backed out
   * of, an app, another tab (§10.71). India dispatched `cart:reload` and let
   * Kalles re-fetch itself; Dawn's drawer has no such listener, so fetch the
   * sections and render them the same way an add response would.
   */
  function reload() {
    if (onCartPage()) return refreshCartPage();
    var sections = sectionsToRender();
    if (!sections.length) return refreshCount();
    return fetch('/cart?sections=' + sections.join(','))
      .then(function (r) { return r.json(); })
      .then(function (sectionHTML) {
        var d = drawer();
        if (!d) return refreshCount();
        // renderContents expects { sections: {...} } and also re-opens the
        // drawer, which is wrong for a background refresh — render the section
        // bodies directly instead and leave the open state alone.
        d.getSectionsToRender().forEach(function (sec) {
          var el = sec.selector ? document.querySelector(sec.selector)
                                : document.getElementById(sec.id);
          if (!el || !sectionHTML[sec.id]) return;
          el.innerHTML = d.getSectionInnerHTML(sectionHTML[sec.id], sec.selector);
        });
        return refreshCount();
      })
      .catch(function () { return refreshCount(); });
  }

  /* `cart:reload` was Kalles' "re-fetch yourself" event. Three files in this
     tree still dispatch it and NOTHING listened, so each was a no-op: the Buy
     Now hand-off (§10.71) and the collection/wishlist fallbacks all fired into
     the void and the drawer kept its page-load HTML. The dispatchers are routed
     through reload() directly now; this listener stays as the net, so a future
     caller that dispatches the event gets the behaviour its name promises
     instead of silence (§10.54 -- a hook with no counterpart is dead markup). */
  document.addEventListener('cart:reload', function () { reload(); });

  window.KitstersCart = {
    drawer: drawer,
    sectionsToRender: sectionsToRender,
    notify: notify,
    refreshCount: refreshCount,
    reload: reload,
    onCartPage: onCartPage,
    refreshCartPage: refreshCartPage,
    addToCart: addToCart
  };
})();
